import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import { Field, FormikErrors, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import promoCodes from "../_shared/constants/promoCodes";
import countries from "../../../_shared/constants/countries";
import "./PayWithCard.scss";
import { FormValues } from "../_shared/models/FormValues";
import { ProductDetails } from "../_shared/models/ProductDetails";
import InputField from "../../../_shared/components/InputField/InputField";
import { stripeKey } from "../../../env";
import PaymentSummary from "./components/PaymentSummary/PaymentSummary";
import PromoInput from "./components/PromoInput/PromoInput";
import TermsOfService from "./components/TermsOfService/TermsOfService";
import SelectField from "../../../_shared/components/SelectField/SelectField";
import { useLocation } from "react-router-dom";
import Button from "_shared/components/Button/Button";
import CustomModal from "_shared/components/Modal/Modal";

const stripePromise = loadStripe(stripeKey);

// Stripe element configuration options
const elementOptions = {
  style: {
    base: {
      color: "#ffffff",
      "::placeholder": {
        color: "#ffffff",
      },
    },
    invalid: {
      color: "#ffffff",
    },
  },
};

Modal.setAppElement("#root");

interface Props {
  productDetails: Record<string, ProductDetails>;
  onBack: () => void;
  onNext(
    values: FormValues,
    errors: FormikErrors<FormValues>,
    stripe: Stripe | null,
    elements: StripeElements | null,
  ): Promise<void>;
}

const PayWithCard: React.FC<Props> = ({
  productDetails,
  onBack,
  onNext: onConfirmAccount,
}) => {
  // 'element' instance used to create the payment element
  const elements = useElements();
  const stripe = useStripe();
  const { values, errors } = useFormikContext<FormValues>();
  const [modalTOS, setModalTOS] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState<ProductDetails>();
  const planPrice =
    selectedPlan?.[`monthly_${values.frequency}_price`] ?? Infinity;

  const [selectedAddons, setSelectedAddons] = useState<ProductDetails[]>();
  const addonsPrice =
    selectedAddons?.reduce((prev, addon) => {
      // get price for addon plan (daily or intraday -- top25 is only daily)
      const price =
        addon[
          `monthly_${
            addon.key === "top25_dashboard" ? "daily" : values.frequency
          }_price`
        ];
      return prev + price;
    }, 0) ?? Infinity;

  useEffect(() => {
    setSelectedPlan(productDetails[values.plan]);
    setSelectedAddons(values.addOns.map((addon) => productDetails[addon]));
  }, [productDetails, values.addOns, values.plan]);

  useEffect(() => {
    // prevent scrolling when TOS modal active
    if (modalTOS) {
      document.body.style.overflow = "hidden";
    } else document.body.style.overflow = "scroll";
  }, [modalTOS]);

  const location = useLocation<any>();

  React.useEffect(() => {
    if (process.env.NODE_ENV === "test") return;
    // Twitter tracking
    (window as any).twq("event", "tw-o90ig-oe3th", {
      value: location.state?.price, // use this to pass the value of the conversion (e.g. 5.00)
      currency: "USD", // use this to pass the currency of the conversion with an ISO 4217 code (e.g. ‘USD’)
      conversion_id: location.state?.sub, // use this to pass a unique ID for the conversion event for deduplication (e.g. order id '1a2b3c')
      email_address: location.state?.email, // use this to pass a user’s email address
      phone_number: location.state?.phone_number, // phone number in E164 standard
    });
  }, [
    location.state?.email,
    location.state?.phone_number,
    location.state?.price,
    location.state?.sub,
  ]);

  // TODO: styles here should be ported to CSS modules
  return (
    <div id="pay-with-card-page">
      <div
        className="card"
        style={
          values.addOns.length <= 2
            ? { height: "910px !important" }
            : { height: "970px !important" }
        }
      >
        {(selectedPlan?.key === "free_plan" ||
          (selectedPlan?.key === "plus_plan" && values.verified42Macro)) &&
        !selectedAddons?.length ? (
          <div className="border-padding">
            <h2
              className="welcome-text"
              style={{ fontWeight: 800, marginBottom: 5, marginTop: 5 }}
            >
              No payment due
            </h2>
            <h2 className="welcome-text" style={{ fontSize: 12 }}>
              Please complete your registration to access your Longbow
              dashboard.
            </h2>
            {/* Terms of Service checkbox */}
            <label
              style={{
                display: "flex",
                marginTop: 18,
                fontSize: "13px",
                marginLeft: "25px",
              }}
            >
              <div data-testid="field-acceptsTerms">
                <Field type="checkbox" name="acceptsTerms" />
              </div>
              <div style={{ marginLeft: 8, marginTop: 5 }}>
                I agree to the&nbsp;
                <span
                  style={{ color: "#75ce41", cursor: "pointer" }}
                  onClick={() => setModalTOS(true)}
                >
                  Terms and Conditions
                </span>
                .
              </div>
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                margin: "2rem 0",
              }}
            >
              <Button
                data-testid="button-signup"
                type="submit"
                onClick={() => {
                  onConfirmAccount(values, errors, stripe, elements);
                }}
                fullWidth
              >
                Sign Up
              </Button>
              <Button onClick={onBack} variant="secondary" fullWidth>
                Back
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-padding">
            <h2
              className="welcome-text"
              style={{ fontWeight: 800, marginBottom: 5, marginTop: 5 }}
            >
              Payment
            </h2>
            <h2 className="welcome-text" style={{ fontSize: 12 }}>
              Please verify your purchase and enter your card information below.
            </h2>
            {/* Input Fields */}
            <form>
              <div className="input-container">
                <div data-testid="card-number">
                  <CardNumberElement
                    options={elementOptions}
                    className="input-text"
                  />
                  <div className="card-icons">
                    <i className="fab fa-cc-visa"></i>
                    <i className="fab fa-cc-amex"></i>
                    <i className="fab fa-cc-mastercard"></i>
                    <i className="fab fa-cc-discover"></i>
                  </div>
                </div>
                <div className="two-column">
                  <div data-testid="card-expiry" style={{ width: "50%" }}>
                    <CardExpiryElement
                      options={elementOptions}
                      className="input-text"
                    />
                  </div>
                  <div data-testid="card-cvc" style={{ width: "50%" }}>
                    <CardCvcElement
                      options={elementOptions}
                      className="input-text"
                    />
                  </div>
                </div>
                {/* TODO: disallowing special characters has been removed here */}
                <InputField name="billingName" label="Name on card" dark />
                <SelectField
                  name="billingCountry"
                  label="Country"
                  options={countries}
                  dark
                />
                <InputField name="billingZipCode" label="Zip code" dark />
              </div>
            </form>

            <PaymentSummary
              selectedPlan={selectedPlan!}
              planPrice={values.verified42Macro ? 0 : planPrice}
              selectedAddons={selectedAddons || []}
              addonsPrice={addonsPrice}
            />
            <br />
            <PromoInput
              promoCodes={promoCodes}
              planPrice={values.verified42Macro ? 0 : planPrice}
              addonsPrice={addonsPrice}
            />

            {/* Terms of Service checkbox */}
            <label
              style={{
                display: "flex",
                marginTop: 18,
                fontSize: "13px",
                marginLeft: "25px",
              }}
            >
              <div data-testid="field-acceptsTerms">
                <Field type="checkbox" name="acceptsTerms" />
              </div>
              <div style={{ marginLeft: 8, marginTop: 5 }}>
                I agree to the&nbsp;
                <span
                  style={{ color: "#75ce41", cursor: "pointer" }}
                  onClick={() => setModalTOS(true)}
                >
                  Terms and Conditions
                </span>
                .
              </div>
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                margin: "2rem 0",
              }}
            >
              <Button
                data-testid="button-signup"
                type="submit"
                onClick={() => {
                  onConfirmAccount(values, errors, stripe, elements);
                }}
                fullWidth
              >
                Sign Up
              </Button>
              <Button onClick={onBack} variant="secondary" fullWidth>
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Terms of Service Modal */}
      <CustomModal
        open={modalTOS}
        onClose={() => {
          setModalTOS(false);
        }}
      >
        <div style={{ overflowY: "auto" }}>
          <TermsOfService />
        </div>
      </CustomModal>
    </div>
  );
};

export default function WithStripe(props: Props) {
  return (
    <Elements stripe={stripePromise}>
      <PayWithCard {...props} />
    </Elements>
  );
}
